import { AlertService } from "@/services/alert.service";
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import {
  HomeViewType,
  SharingVisitModeType,
  User,
  UserRole,
  UsersService,
} from "@eemo/api-client";
import { Subscription } from "rxjs";

import { TooltipDirective } from "@/directives/tooltip/tooltip.directive";
import { EnumHelper } from "@/helpers/enumHelper";
import { UserHelper } from "@/helpers/user.helper";
import { AppConfigService } from "@/services/app-config.service";
import { NgIf, NgTemplateOutlet } from "@angular/common";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { TranslateModule } from "@ngx-translate/core";
import { ModalBaseComponent } from "../base/modal-base/modal-base.component";
import { InfoDialogComponent } from "../info-dialog/info-dialog.component";
import { SelectComponent } from "../ui-forms/select/select.component";

export class PreferencesUpdateDialogData {
  user: User;
}

@Component({
  selector: "app-preferences-update-dialog",
  templateUrl: "./preferences-update-dialog.component.html",
  styleUrls: ["./preferences-update-dialog.component.scss"],
  standalone: true,
  imports: [
    ModalBaseComponent,
    NgTemplateOutlet,
    MatDialogModule,
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    SelectComponent,
    MatCheckboxModule,
    TranslateModule,
    MatRadioModule,
    MatInputModule,
    MatIconModule,
    TooltipDirective,
  ],
})
export class PreferencesUpdateDialogComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  dialogTitle: string;
  loading = false;

  viewList: HomeViewType[] = EnumHelper.getApiEnumList(HomeViewType);

  sub = new Subscription();
  subPreferences = new Subscription();

  canSeeDefaultVisitModeType = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PreferencesUpdateDialogData,
    private fb: UntypedFormBuilder,
    private userService: UsersService,
    private userHelper: UserHelper,
    private alertService: AlertService,
    public dialogRef: MatDialogRef<PreferencesUpdateDialogComponent>,
    private appConfigService: AppConfigService,
    private dialog: MatDialog
  ) {
    this.dialogTitle = "Modifier mes préférences";
  }

  get form_homeView() {
    return this.form.get("homeView") as FormControl<string>;
  }

  get form_notificationSendMail() {
    return this.form.get("notificationSendMail") as FormControl<boolean>;
  }

  get form_notificationSendAppPush() {
    return this.form.get("notificationSendAppPush") as FormControl<boolean>;
  }

  get form_defaultVisitModeType() {
    return this.form.get("defaultVisitModeType") as FormControl<string>;
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      homeView: [HomeViewType.CARD, Validators.required],
      notificationSendMail: [true, Validators.required],
      notificationSendAppPush: [true, Validators.required],
      defaultVisitModeType: [SharingVisitModeType.FULL, Validators.required],
    });
    this.subPreferences = this.userService
      .getUserPreferences(this.data.user.uid)
      .subscribe((preferences) => {
        this.form.patchValue(preferences);
      });
    this.canSeeDefaultVisitModeType = [
      UserRole.ADMIN,
      UserRole.REAL_ESTATE,
    ].includes(this.userHelper.currentUser.role);
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.subPreferences.unsubscribe();
  }

  onSubmit() {
    if (this.form.valid) {
      this.loading = true;

      // reset alerts on submit
      this.alertService.clear();

      this.sub = this.userService
        .updateUserPreferences(this.data.user.uid, this.form.value)
        .subscribe({
          next: () => {
            this.alertService.success("Préférences mises à jour", true);
            this.appConfigService.userPreferences = this.form.value;
            this.loading = false;
            this.dialogRef.close(true);
          },
          error: () => {
            this.loading = false;
          },
        });
    }
  }

  onNoClick(): void {
    this.dialogRef.close(false);
  }

  showHomeViewInfo() {
    this.dialog.open(InfoDialogComponent, {
      maxWidth: "30rem",
      data: {
        title: "Vue d'accueil par défaut",
        content:
          '<p class="info-title">Choisissez la vue d\'accueil que vous préférez pour visualiser vos biens :</p>' +
          "<ul>" +
          "<li><strong>Vue grille</strong> : de manière synthétique, idéal quand vous avez un nombre limité de biens</li>" +
          "<li><strong>Vue Carte</strong> : sur une carte, idéal quand vos biens ont des localisations éloignées</li>" +
          "<li><strong>Vue liste</strong> : dans une liste avec filtre de recherche, idéal quand vous avez un nombre important de biens</li>" +
          "<ul>",
      },
    });
  }

  showNotificationMailInfo() {
    this.dialog.open(InfoDialogComponent, {
      maxWidth: "30rem",
      data: {
        title: "Notification par mail",
        content:
          '<p class="info-title">Choisissez si vous ne souhaitez plus recevoir de notification par mail...</p>' +
          '<p class="info-title">Certaines notifications essentielles comme la validation des bons de visite resteront actives.</p>',
      },
    });
  }

  showNotificationAppPushInfo() {
    this.dialog.open(InfoDialogComponent, {
      maxWidth: "30rem",
      data: {
        title: "Notification sur application smartphone",
        content:
          '<p class="info-title">Choisissez si vous ne souhaitez plus recevoir de notification sur votre application smartphone...</p>' +
          '<p class="info-title">Vous pouvez également gérer ces notifications depuis les préférences de votre téléphone.</p>',
      },
    });
  }

  showDefaultVisiteModeTypeInfo() {
    this.dialog.open(InfoDialogComponent, {
      maxWidth: "30rem",
      data: {
        title: "Partage d'un bien : option visite par défaut",
        content:
          '<p class="info-title">Lorsque vous partagez un bien, vous pouvez choisir votre mode d\'option visite par défaut :</p>' +
          "<ul>" +
          "<li><strong>Complète</strong> : une validation du bon de visite ET de l'ERP sera demandé à votre client</li>" +
          "<li><strong>Simple</strong> : seule la validation du bon de visite sera demandé. L'ERP sera simplement joint au mail de validation pour information.</li>" +
          "<ul>" +
          '<p class="info-title mt-2">Vous garder cependant la possibilité de faire différemment au cas par cas.</p>',
      },
    });
  }
}
